/**
 * API Client pour les Exécutions Terraform
 *
 * Endpoints:
 * - Créer exécutions (plan, apply, destroy)
 * - Lister et filtrer exécutions
 * - Obtenir détails, logs, résultats
 * - Approuver, annuler, retry
 * - Statistiques
 */

import api from './index';

export interface Execution {
  id: string;
  run_id: string;
  architecture_id: string;
  architecture_name?: string;
  architecture_version_id: string;  // Alias for version_id
  version_id: string;
  version_number?: number;
  environment_id: string;
  environment_name?: string;
  environment_type?: 'dev' | 'staging' | 'prod';
  action: 'plan' | 'apply' | 'destroy';
  status: 'pending' | 'queued' | 'running' | 'success' | 'succeeded' | 'failed' | 'cancelled' | 'pending_approval' | 'approved';
  initiated_by: string;
  initiated_by_name?: string;
  plan_run_id?: string;
  engine_type?: 'azure-devops' | 'kubernetes-job' | 'mock';
  ado_build_id?: string;
  k8s_job_name?: string;
  queue_job_id?: string;
  external_job_id?: string;
  plan_output?: any;
  apply_results?: any;
  terraform_output?: any;
  terraform_plan_summary?: any;
  resources_created?: number;
  resources_updated?: number;
  resources_deleted?: number;
  error_message?: string;
  exit_code?: number;
  terraform_version?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  duration_seconds?: number;
}

export interface ExecutionListResponse {
  executions: Execution[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExecutionStats {
  total: number;
  by_status: Record<string, number>;
  by_action: Record<string, number>;
  by_environment: Record<string, number>;
  success_rate: number;
  average_duration_seconds: number;
}

export interface ExecutionMetrics {
  period: {
    days: number;
    start_date: string;
    end_date: string;
  };
  global: {
    total: number;
    succeeded: number;
    failed: number;
    in_progress: number;
    success_rate: number;
    avg_duration_seconds: number;
    median_duration_seconds: number;
  };
  by_action: Array<{
    action: string;
    count: number;
    succeeded: number;
    avg_duration: number;
  }>;
  by_environment: Array<{
    name: string;
    type: string;
    color?: string;
    count: number;
    succeeded: number;
    avg_duration: number;
  }>;
  top_architectures: Array<{
    id: string;
    name: string;
    count: number;
    succeeded: number;
    failed: number;
  }>;
  trends: Array<{
    date: string;
    total: number;
    succeeded: number;
    failed: number;
    avg_duration: number;
  }>;
  recent_executions: Array<{
    run_id: string;
    action: string;
    status: string;
    architecture_name?: string;
    environment_name?: string;
    environment_type?: string;
    initiated_by_name?: string;
    created_at: string;
    duration_seconds?: number;
  }>;
  by_hour: Array<{
    hour: number;
    count: number;
  }>;
}

export interface ExecutionMetricsComparison {
  current: ExecutionMetrics['global'];
  previous: {
    total: number;
    succeeded: number;
    success_rate: number;
  };
  comparison: {
    total_change: number;
    total_change_percent: string;
    success_rate_change: string;
  };
}

export interface CreateExecutionRequest {
  architecture_id: string;
  environment_id: string;
  version_id: string;
  plan_run_id?: string; // Required for apply
}

export interface ExecutionLog {
  timestamp: string;
  level: 'info' | 'warning' | 'error' | 'debug';
  message: string;
  source?: string;
}

export interface ExecutionArtifact {
  id: string;
  run_id: string;
  artifact_type: 'bundle' | 'plan' | 'state' | 'logs' | 'manifest';
  storage_url: string;
  size_bytes: number;
  content_hash?: string;
  is_deleted: boolean;
  created_at: string;
  expires_at?: string;
}

export interface ExecutionStep {
  id: string;
  run_id: string;
  step_name: string;
  step_order: number;
  step_display_name: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped' | 'waiting_approval';
  started_at?: string;
  completed_at?: string;
  duration_seconds?: number;
  logs?: string;
  error_message?: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface StepStatistics {
  [step_name: string]: {
    step_display_name: string;
    execution_count: number;
    avg_duration: string;
    min_duration: string;
    max_duration: string;
    median_duration: string;
  };
}

/**
 * Créer une exécution (plan, apply, destroy)
 */
export const createExecution = async (data: CreateExecutionRequest & { action: 'plan' | 'apply' | 'destroy' }): Promise<Execution> => {
  // Transform snake_case to camelCase for backend
  const payload = {
    architectureId: data.architecture_id,
    environmentId: data.environment_id,
    versionId: data.version_id,
    action: data.action,
    planRunId: data.plan_run_id,
  };
  const response = await api.post('/api/executions', payload);
  return response.data.execution || response.data;
};

/**
 * Créer une exécution plan
 */
export const createPlanExecution = async (data: CreateExecutionRequest): Promise<Execution> => {
  return createExecution({ ...data, action: 'plan' });
};

/**
 * Créer une exécution apply
 */
export const createApplyExecution = async (data: CreateExecutionRequest): Promise<Execution> => {
  return createExecution({ ...data, action: 'apply' });
};

/**
 * Créer une exécution destroy
 */
export const createDestroyExecution = async (data: CreateExecutionRequest): Promise<Execution> => {
  return createExecution({ ...data, action: 'destroy' });
};

/**
 * Lister les exécutions avec filtres et pagination
 */
export const listExecutions = async (params?: {
  architectureId?: string;
  environmentId?: string;
  status?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}): Promise<ExecutionListResponse> => {
  const response = await api.get('/api/executions', { params });
  // Adapt backend response to frontend interface
  const data = response.data;
  return {
    executions: data.executions || [],
    total: data.total || data.pagination?.total || data.pagination?.count || data.executions?.length || 0,
    limit: data.pagination?.limit || params?.limit || 50,
    offset: data.pagination?.offset || params?.offset || 0
  };
};

/**
 * Obtenir les détails d'une exécution
 */
export const getExecution = async (runId: string): Promise<Execution> => {
  const response = await api.get(`/api/executions/${runId}`);
  return response.data.execution || response.data;
};

/**
 * Approuver une exécution
 */
export const approveExecution = async (runId: string, comment?: string): Promise<{ message: string }> => {
  const response = await api.post(`/api/executions/${runId}/approve`, { comment });
  return response.data;
};

/**
 * Rejeter une exécution (refus d'approbation)
 */
export const rejectExecution = async (runId: string, reason: string): Promise<{ message: string }> => {
  const response = await api.post(`/api/executions/${runId}/reject`, { reason });
  return response.data;
};

/**
 * Annuler une exécution
 */
export const cancelExecution = async (runId: string, reason?: string): Promise<{ message: string }> => {
  const response = await api.post(`/api/executions/${runId}/cancel`, { reason });
  return response.data;
};

/**
 * Retry une exécution échouée
 */
export const retryExecution = async (runId: string): Promise<Execution> => {
  const response = await api.post(`/api/executions/${runId}/retry`);
  return response.data;
};

/**
 * Obtenir les logs d'une exécution
 */
export const getExecutionLogs = async (runId: string): Promise<ExecutionLog[]> => {
  const response = await api.get(`/api/executions/${runId}/logs`);

  const data = response.data.logs || response.data;

  // Si pas de logs disponibles
  if (!data || (typeof data === 'object' && data.stdout === null && data.stderr === null)) {
    return [];
  }

  // Si c'est un objet avec stdout/stderr
  if (typeof data === 'object' && (data.stdout || data.stderr)) {
    const logs: ExecutionLog[] = [];

    if (data.stdout) {
      const lines = data.stdout.split('\n').filter((line: string) => line.trim());
      lines.forEach((line: string) => {
        logs.push({
          timestamp: new Date().toISOString(),
          level: 'info' as const,
          message: line,
          source: 'stdout'
        });
      });
    }

    if (data.stderr) {
      const lines = data.stderr.split('\n').filter((line: string) => line.trim());
      lines.forEach((line: string) => {
        logs.push({
          timestamp: new Date().toISOString(),
          level: 'error' as const,
          message: line,
          source: 'stderr'
        });
      });
    }

    return logs;
  }

  // Si c'est une string, parser les lignes
  if (typeof data === 'string') {
    const lines = data.split('\n').filter(line => line.trim());
    return lines.map(line => ({
      timestamp: new Date().toISOString(),
      level: 'info' as const,
      message: line,
      source: 'terraform'
    }));
  }

  // Si c'est déjà un tableau
  if (Array.isArray(data)) {
    return data;
  }

  return [];
};

/**
 * Stream les logs d'une exécution (polling)
 */
export const streamExecutionLogs = (
  runId: string,
  onLog: (log: ExecutionLog) => void,
  onError?: (error: Error) => void,
  interval: number = 2000
): { stop: () => void } => {
  let lastLogCount = 0;
  let intervalId: NodeJS.Timeout;
  let stopped = false;

  const poll = async () => {
    if (stopped) return;

    try {
      const logs = await getExecutionLogs(runId);

      // Envoyer seulement les nouveaux logs
      if (logs.length > lastLogCount) {
        const newLogs = logs.slice(lastLogCount);
        newLogs.forEach(log => onLog(log));
        lastLogCount = logs.length;
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
    }
  };

  // Premier poll immédiat
  poll();

  // Puis polling régulier
  intervalId = setInterval(poll, interval);

  return {
    stop: () => {
      stopped = true;
      clearInterval(intervalId);
    }
  };
};

/**
 * Obtenir le plan output
 */
export const getPlanOutput = async (runId: string): Promise<any> => {
  const response = await api.get(`/api/executions/${runId}/plan-output`);
  return response.data.planOutput || response.data;
};

/**
 * Obtenir le state Terraform
 */
export const getTerraformState = async (runId: string): Promise<any> => {
  const response = await api.get(`/api/executions/${runId}/state`);
  return response.data.state || response.data;
};

/**
 * Lister les artefacts d'une exécution
 */
export const getExecutionArtifacts = async (runId: string): Promise<ExecutionArtifact[]> => {
  const response = await api.get(`/api/executions/${runId}/artifacts`);
  return response.data.artifacts || response.data || [];
};

/**
 * Obtenir les étapes d'une exécution (timeline step-by-step)
 */
export const getExecutionSteps = async (runId: string): Promise<ExecutionStep[]> => {
  const response = await api.get(`/api/executions/${runId}/steps`);
  return response.data.steps || [];
};

/**
 * Obtenir les statistiques de durées moyennes par step pour une architecture/action
 */
export const getStepStatistics = async (
  architectureId: string,
  action: 'plan' | 'apply' | 'destroy',
  limit: number = 10
): Promise<StepStatistics> => {
  const response = await api.get('/api/executions/statistics/steps', {
    params: { architectureId, action, limit }
  });
  return response.data.statistics || {};
};

/**
 * Obtenir les statistiques d'exécutions
 */
export const getExecutionStats = async (params?: {
  architecture_id?: string;
  environment_id?: string;
  period?: '7d' | '30d' | '90d';
}): Promise<ExecutionStats> => {
  const response = await api.get('/api/executions/stats', { params });
  return response.data.stats || response.data;
};

/**
 * Obtenir les métriques agrégées pour le dashboard Monitoring
 */
export const getExecutionMetrics = async (params?: {
  days?: number;
  comparison?: boolean;
}): Promise<ExecutionMetrics | ExecutionMetricsComparison> => {
  const response = await api.get('/api/executions/metrics', {
    params: {
      days: params?.days,
      comparison: params?.comparison ? 'true' : undefined
    }
  });
  return response.data.metrics;
};

/**
 * Polling du statut d'une exécution jusqu'à complétion
 */
export const pollExecutionStatus = async (
  runId: string,
  onStatusChange: (execution: Execution) => void,
  onComplete: (execution: Execution) => void,
  onError?: (error: Error) => void,
  interval: number = 3000,
  timeout: number = 300000 // 5 minutes
): Promise<{ stop: () => void }> => {
  let stopped = false;
  let intervalId: NodeJS.Timeout;
  let timeoutId: NodeJS.Timeout;
  let lastStatus: string | null = null;

  const poll = async () => {
    if (stopped) return;

    // Validate runId before making API call
    if (!runId || runId === 'undefined') {
      console.warn('pollExecutionStatus: Invalid runId, stopping polling');
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      return;
    }

    try {
      const execution = await getExecution(runId);

      // Notifier le changement de statut
      if (execution.status !== lastStatus) {
        lastStatus = execution.status;
        onStatusChange(execution);
      }

      // Vérifier si l'exécution est terminée
      const completedStatuses = ['succeeded', 'failed', 'cancelled'];
      if (completedStatuses.includes(execution.status)) {
        stopped = true;
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        onComplete(execution);
      }
    } catch (error) {
      if (onError) {
        onError(error as Error);
      }
    }
  };

  // Premier poll immédiat
  poll();

  // Polling régulier
  intervalId = setInterval(poll, interval);

  // Timeout
  timeoutId = setTimeout(() => {
    stopped = true;
    clearInterval(intervalId);
    if (onError) {
      onError(new Error('Polling timeout exceeded'));
    }
  }, timeout);

  return {
    stop: () => {
      stopped = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    }
  };
};

export interface ExecutionCapacity {
  plan:             string;
  type:             'enterprise' | 'business' | 'shared';
  available:        boolean;
  total:            number;
  used:             number;
  free:             number;
  agents_online?:   number;
  effective_limit?: number;
  provision_status?: string;
  message?:         string;
}

export const getExecutionCapacity = async (): Promise<ExecutionCapacity> => {
  const response = await api.get('/api/executions/capacity');
  return response.data;
};

export async function resetTfstate(architectureId: string, environmentId: string): Promise<{ success: boolean; message: string; tfstateKey: string }> {
  const response = await api.post('/api/executions/reset-tfstate', { architectureId, environmentId });
  return response.data;
}

export default {
  createPlanExecution,
  createApplyExecution,
  createDestroyExecution,
  listExecutions,
  getExecution,
  approveExecution,
  rejectExecution,
  cancelExecution,
  retryExecution,
  getExecutionLogs,
  streamExecutionLogs,
  getPlanOutput,
  getTerraformState,
  getExecutionArtifacts,
  getExecutionStats,
  getExecutionMetrics,
  pollExecutionStatus,
  getExecutionCapacity,
  resetTfstate,
};
